import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import * as XLSX from "xlsx";
import {
  fetchClockEntries,
  fetchCorrectionRequests,
} from "../../../api/TimeTracking/timeTrackingApi";
import {
  formatGeoSourceLabel,
  geoCellLabel,
  openStreetMapLink,
} from "../../../utils/clockGeolocation";
import {
  ttHistoryActionBtnSx,
  ttHistoryPunchCellSx,
  ttHistoryTableContainerSx,
  ttHistoryTableSx,
  ttKpiChipSx,
  ttMobileActionBtnSx,
  ttMobileAvatarSx,
  ttMobilePunchBlockSx,
  ttMobileSessionCardSx,
  ttSectionTitleSx,
  ttToolbarButtonSx,
  ttToolbarFieldSx,
  ttToolbarSx,
} from "./timeTrackingStyles";
import { employeeInitials } from "./vacationCalendarUtils";
import TabContentShell from "./TabContentShell";
import ClockAdjustDialog from "./ClockAdjustDialog";
import CorrectionRequestDialog from "./CorrectionRequestDialog";
import {
  computeHistoryWorkStats,
  buildHistorySessions,
  formatMinutes,
  formatTime,
  formatDate,
} from "./historyWorkedHours";

const EXPECTED_DAILY_MINUTES = 8 * 60;

const SOURCE_LABELS = {
  mobile: "Móvil",
  web: "Web",
  manual_correction: "Manual RRHH",
};

function formatSourceLabel(source) {
  return SOURCE_LABELS[source] || source || "—";
}

function hoursChipColor(minutes) {
  return minutes > EXPECTED_DAILY_MINUTES ? "error" : "success";
}

function shouldShowGeo(entry) {
  if (!entry?.geoSource) return false;
  if (entry.source === "manual_correction") return false;
  return true;
}

function geoStatusLine(entry, { compact = false } = {}) {
  if (!shouldShowGeo(entry)) return null;

  const link = openStreetMapLink(entry.latitude, entry.longitude);
  const label = geoCellLabel(entry);
  const isOk = entry.geoSource === "browser" && entry.latitude != null;
  const color = isOk
    ? "success.main"
    : entry.geoSource === "denied"
      ? "warning.main"
      : "text.secondary";

  const textProps = {
    variant: "caption",
    sx: {
      color,
      lineHeight: 1.2,
      fontSize: compact ? "0.62rem" : undefined,
    },
  };

  return (
    <Stack
      direction="row"
      spacing={0.25}
      alignItems="center"
      sx={{ mt: compact ? 0.1 : 0.15 }}
    >
      <LocationOnOutlinedIcon
        sx={{ fontSize: compact ? 11 : 13, color, flexShrink: 0 }}
      />
      {link ? (
        <Typography
          {...textProps}
          component="a"
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ ...textProps.sx, textDecoration: "underline" }}
        >
          {label}
        </Typography>
      ) : (
        <Typography {...textProps}>{label}</Typography>
      )}
    </Stack>
  );
}

function formatDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-ES", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function sessionStatusLabel(session) {
  if (session.status === "correction") return "Corrección";
  if (session.isOpen) return "Abierta";
  if (session.status === "manual") return "Manual RRHH";
  return "Vigente";
}

function sessionStatusColor(session) {
  if (session.status === "correction") return "warning";
  if (session.isOpen) return "info";
  if (session.status === "manual") return "info";
  return "default";
}

function entryCell(entry, theme, { punchColor, PunchIcon } = {}) {
  if (!entry) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  const useDesktopPunch = punchColor && PunchIcon;

  if (useDesktopPunch) {
    return (
      <Box sx={ttHistoryPunchCellSx(theme, punchColor)}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <PunchIcon sx={{ fontSize: 15, color: punchColor, opacity: 0.9 }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 800,
              fontVariantNumeric: "tabular-nums",
              letterSpacing: 0.2,
              lineHeight: 1.1,
            }}
          >
            {formatTime(entry.recordedAt)}
          </Typography>
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ lineHeight: 1.25, pl: 2.25 }}
        >
          {formatSourceLabel(entry.source)}
        </Typography>
        {geoStatusLine(entry)}
      </Box>
    );
  }

  return (
    <Stack spacing={0.2}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {formatTime(entry.recordedAt)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
        {formatSourceLabel(entry.source)}
      </Typography>
      {geoStatusLine(entry)}
    </Stack>
  );
}

function canActOn(entry) {
  return entry && !entry.isSuperseded && !entry.isCorrection;
}

function hasPendingCorrection(entry, pendingEntryIds) {
  return Boolean(entry?.id && pendingEntryIds?.has(entry.id));
}

function requestedActionBtnSx(theme, { clickable = false } = {}) {
  return {
    ...ttMobileActionBtnSx,
    borderColor: alpha(theme.palette.error.main, 0.55),
    color: theme.palette.error.main,
    bgcolor: alpha(theme.palette.error.main, 0.1),
    cursor: clickable ? "pointer" : "default",
    "&.Mui-disabled": {
      borderColor: alpha(theme.palette.error.main, 0.55),
      color: theme.palette.error.main,
      opacity: 1,
    },
    ...(clickable
      ? {
          "&:hover": {
            bgcolor: alpha(theme.palette.error.main, 0.16),
            borderColor: alpha(theme.palette.error.main, 0.7),
          },
        }
      : {}),
  };
}

function sessionAccentColor(session, theme) {
  if (session.status === "correction") return theme.palette.warning.main;
  if (session.isOpen) return theme.palette.info.main;
  if (session.status === "manual") return theme.palette.info.main;
  return theme.palette.success.main;
}

function mobileEntryBlock(entry, theme, punchColor) {
  if (!entry) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ fontWeight: 600 }}>
        —
      </Typography>
    );
  }

  const link = shouldShowGeo(entry) ? openStreetMapLink(entry.latitude, entry.longitude) : null;
  const geoLabel = shouldShowGeo(entry) ? geoCellLabel(entry) : null;
  const isOk = entry.geoSource === "browser" && entry.latitude != null;
  const geoColor = isOk
    ? theme.palette.success.main
    : entry.geoSource === "denied"
      ? theme.palette.warning.main
      : theme.palette.text.secondary;

  return (
    <Stack spacing={0.35}>
      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1, color: punchColor }}>
        {formatTime(entry.recordedAt)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {formatSourceLabel(entry.source)}
      </Typography>
      {geoLabel ? (
        <Stack direction="row" spacing={0.35} alignItems="center">
          <LocationOnOutlinedIcon sx={{ fontSize: 12, color: geoColor }} />
          {link ? (
            <Typography
              variant="caption"
              component="a"
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: geoColor, fontWeight: 600, textDecoration: "none" }}
            >
              {geoLabel}
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ color: geoColor, fontWeight: 600 }}>
              {geoLabel}
            </Typography>
          )}
        </Stack>
      ) : null}
    </Stack>
  );
}

const ACTION_SHORT_LABELS = {
  in: "Entrada",
  out: "Salida",
  "req-in": "Entrada",
  "req-out": "Salida",
};

function SessionActions({
  session,
  canRrhh,
  canFichaje,
  onCorrect,
  onRequestCorrect,
  onGoToCorrections,
  pendingEntryIds,
  stacked = false,
  theme,
}) {
  const actions = [];

  if (canRrhh) {
    if (canActOn(session.clockIn)) {
      actions.push(
        hasPendingCorrection(session.clockIn, pendingEntryIds)
          ? {
              key: "in",
              label: "Corrección solicitada",
              requested: true,
              navigate: true,
              onClick: onGoToCorrections,
            }
          : {
              key: "in",
              label: "Corregir entrada",
              onClick: () => onCorrect(session.clockIn),
            },
      );
    }
    if (canActOn(session.clockOut)) {
      actions.push(
        hasPendingCorrection(session.clockOut, pendingEntryIds)
          ? {
              key: "out",
              label: "Corrección solicitada",
              requested: true,
              navigate: true,
              onClick: onGoToCorrections,
            }
          : {
              key: "out",
              label: "Corregir salida",
              onClick: () => onCorrect(session.clockOut),
            },
      );
    }
  } else if (canFichaje) {
    if (canActOn(session.clockIn)) {
      actions.push(
        hasPendingCorrection(session.clockIn, pendingEntryIds)
          ? { key: "req-in", label: "Corrección solicitada", requested: true }
          : {
              key: "req-in",
              label: "Solicitar corrección",
              onClick: () => onRequestCorrect(session.clockIn),
            },
      );
    }
    if (canActOn(session.clockOut)) {
      actions.push(
        hasPendingCorrection(session.clockOut, pendingEntryIds)
          ? { key: "req-out", label: "Corrección solicitada", requested: true }
          : {
              key: "req-out",
              label: "Solicitar salida",
              onClick: () => onRequestCorrect(session.clockOut),
            },
      );
    }
  }

  if (!actions.length) return null;

  if (stacked) {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: actions.length > 1 ? "1fr 1fr" : "1fr",
          gap: 0.75,
        }}
      >
        {actions.map((action) => (
          <Button
            key={action.key}
            variant="outlined"
            size="small"
            disabled={action.requested && !action.navigate}
            startIcon={
              action.requested ? null : (
                <EditOutlinedIcon sx={{ fontSize: "0.95rem !important" }} />
              )
            }
            onClick={action.onClick}
            sx={
              action.requested
                ? requestedActionBtnSx(theme, { clickable: action.navigate })
                : {
                    ...ttMobileActionBtnSx,
                    borderColor: alpha(theme.palette.primary.main, 0.35),
                    color: theme.palette.primary.dark,
                    bgcolor: alpha(theme.palette.background.paper, 0.7),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                    },
                  }
            }
          >
            {action.label}
          </Button>
        ))}
      </Box>
    );
  }

  return (
    <Stack spacing={0.5} alignItems="stretch" sx={{ width: 118 }}>
      {actions.map((action) => (
        <Tooltip key={action.key} title={action.label} arrow placement="left">
          <span style={{ display: "block" }}>
            <Button
              size="small"
              variant="outlined"
              fullWidth
              disabled={action.requested && !action.navigate}
              onClick={action.onClick}
              startIcon={
                action.requested ? null : (
                  <EditOutlinedIcon sx={{ fontSize: "0.85rem !important" }} />
                )
              }
              sx={
                action.requested
                  ? requestedActionBtnSx(theme, { clickable: action.navigate })
                  : ttHistoryActionBtnSx(theme)
              }
            >
              {action.requested
                ? "Pendiente"
                : ACTION_SHORT_LABELS[action.key] || action.label}
            </Button>
          </span>
        </Tooltip>
      ))}
    </Stack>
  );
}

function HistorySessionCard({
  session,
  theme,
  showEmployeeCol,
  canRrhh,
  canFichaje,
  pendingEntryIds,
  onCorrect,
  onRequestCorrect,
  onGoToCorrections,
}) {
  const sessionDate = formatDate(
    session.clockIn?.recordedAt || session.clockOut?.recordedAt,
  );
  const workMode =
    session.clockIn?.workMode || session.clockOut?.workMode || "—";
  const accent = sessionAccentColor(session, theme);
  const employeeName = session.employee?.username || String(session.userId);
  const inColor = theme.palette.success.main;
  const outColor = theme.palette.error.main;
  const hasActions = canRrhh || canFichaje;

  return (
    <Box sx={ttMobileSessionCardSx(theme, accent)}>
      <Box sx={{ pl: 1.75, pr: 1.5, pt: 1.5, pb: hasActions ? 1.25 : 1.5 }}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
          {showEmployeeCol ? (
            <Box sx={ttMobileAvatarSx(theme, accent)}>
              {employeeInitials(employeeName)}
            </Box>
          ) : null}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {showEmployeeCol ? (
              <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                {employeeName}
              </Typography>
            ) : null}
            <Typography
              variant="body2"
              color={showEmployeeCol ? "text.secondary" : "text.primary"}
              sx={{ fontWeight: showEmployeeCol ? 600 : 800, lineHeight: 1.2 }}
            >
              {sessionDate}
            </Typography>
          </Box>
          <Chip
            label={sessionStatusLabel(session)}
            size="small"
            color={sessionStatusColor(session)}
            sx={{
              height: 24,
              fontWeight: 800,
              fontSize: "0.68rem",
              flexShrink: 0,
              bgcolor: alpha(accent, 0.14),
              border: "1px solid",
              borderColor: alpha(accent, 0.3),
              color:
                session.status === "correction"
                  ? theme.palette.warning.dark
                  : session.isOpen
                    ? theme.palette.info.dark
                    : theme.palette.success.dark,
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 1.25 }}>
          <Box sx={ttMobilePunchBlockSx(theme, inColor)}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.75 }}>
              <LoginOutlinedIcon sx={{ fontSize: 14, color: inColor }} />
              <Typography sx={{ ...ttSectionTitleSx, mb: 0 }}>Entrada</Typography>
            </Stack>
            {mobileEntryBlock(session.clockIn, theme, inColor)}
          </Box>
          <Box sx={ttMobilePunchBlockSx(theme, outColor)}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.75 }}>
              <LogoutOutlinedIcon sx={{ fontSize: 14, color: outColor }} />
              <Typography sx={{ ...ttSectionTitleSx, mb: 0 }}>Salida</Typography>
            </Stack>
            {session.isOpen ? (
              <Chip
                label="Sin salida"
                size="small"
                color="info"
                sx={{
                  height: 24,
                  fontWeight: 800,
                  fontSize: "0.68rem",
                  bgcolor: alpha(theme.palette.info.main, 0.12),
                }}
              />
            ) : (
              mobileEntryBlock(session.clockOut, theme, outColor)
            )}
          </Box>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          sx={{
            p: 1,
            borderRadius: 1.75,
            bgcolor: alpha(theme.palette.background.paper, 0.65),
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.55),
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ ...ttSectionTitleSx, mb: 0.25 }}>Modalidad</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, textTransform: "capitalize" }}>
              {workMode}
            </Typography>
            {session.pauses.length > 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {session.pauses.length} pausa(s)
              </Typography>
            ) : null}
          </Box>
          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
            <Typography sx={{ ...ttSectionTitleSx, mb: 0.25 }}>Horas</Typography>
            {session.workedMinutes > 0 ? (
              <Chip
                label={formatMinutes(session.workedMinutes)}
                size="small"
                color={hoursChipColor(session.workedMinutes)}
                sx={{
                  height: 26,
                  fontWeight: 800,
                  fontSize: "0.75rem",
                  px: 0.5,
                  boxShadow: `0 2px 8px ${alpha(
                    hoursChipColor(session.workedMinutes) === "error"
                      ? theme.palette.error.main
                      : theme.palette.success.main,
                    0.25,
                  )}`,
                }}
              />
            ) : (
              <Typography variant="body2" color="text.disabled" sx={{ fontWeight: 700 }}>
                —
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>

      {hasActions ? (
        <Box
          sx={{
            px: 1.5,
            py: 1.15,
            bgcolor: alpha(theme.palette.background.paper, 0.55),
            borderTop: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.45),
          }}
        >
          <SessionActions
            session={session}
            canRrhh={canRrhh}
            canFichaje={canFichaje}
            pendingEntryIds={pendingEntryIds}
            onCorrect={onCorrect}
            onRequestCorrect={onRequestCorrect}
            onGoToCorrections={onGoToCorrections}
            stacked
            theme={theme}
          />
        </Box>
      ) : null}
    </Box>
  );
}

const HistoryTab = ({
  selectedUserId,
  canRrhh,
  canFichaje = false,
  employees = [],
  onGoToCorrections,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustMode, setAdjustMode] = useState("add");
  const [adjustEntry, setAdjustEntry] = useState(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestMode, setRequestMode] = useState("correct");
  const [requestEntry, setRequestEntry] = useState(null);
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [pendingEntryIds, setPendingEntryIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { from, to, limit: 500 };
      if (selectedUserId) params.userId = selectedUserId;

      const entriesPromise = fetchClockEntries(params);
      const pendingParams = { status: "pending" };
      if (selectedUserId) pendingParams.userId = selectedUserId;
      const pendingPromise =
        canFichaje || canRrhh
          ? fetchCorrectionRequests(pendingParams)
          : Promise.resolve([]);

      const [data, pendingRequests] = await Promise.all([
        entriesPromise,
        pendingPromise,
      ]);
      setRows(data);

      if (canFichaje || canRrhh) {
        setPendingEntryIds(
          new Set(
            pendingRequests
              .filter((r) => r.status === "pending" && r.targetEntryId)
              .map((r) => r.targetEntryId),
          ),
        );
      } else {
        setPendingEntryIds(new Set());
      }
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo cargar el historial");
    } finally {
      setLoading(false);
    }
  }, [from, to, selectedUserId, canFichaje, canRrhh]);

  useEffect(() => {
    load();
  }, [load]);

  const workStats = useMemo(
    () => computeHistoryWorkStats(rows),
    [rows],
  );

  const sessions = useMemo(() => buildHistorySessions(rows), [rows]);

  const exportRows = useMemo(
    () =>
      sessions.map((s) => ({
        Empleado: s.employee?.username || s.userId,
        Fecha: formatDate(s.clockIn?.recordedAt || s.clockOut?.recordedAt),
        Entrada: s.clockIn ? formatDateTime(s.clockIn.recordedAt) : "",
        Salida: s.clockOut ? formatDateTime(s.clockOut.recordedAt) : "",
        Horas: s.workedMinutes > 0 ? formatMinutes(s.workedMinutes) : "",
        Modalidad: s.clockIn?.workMode || s.clockOut?.workMode || "",
        GPSEntrada: s.clockIn ? geoCellLabel(s.clockIn) : "",
        EstadoGPSEntrada: s.clockIn
          ? formatGeoSourceLabel(s.clockIn.geoSource)
          : "",
        LatitudEntrada: s.clockIn?.latitude ?? "",
        LongitudEntrada: s.clockIn?.longitude ?? "",
        GPSSalida: s.clockOut ? geoCellLabel(s.clockOut) : "",
        EstadoGPSSalida: s.clockOut
          ? formatGeoSourceLabel(s.clockOut.geoSource)
          : "",
        LatitudSalida: s.clockOut?.latitude ?? "",
        LongitudSalida: s.clockOut?.longitude ?? "",
        Pausas: s.pauses.length,
        Estado: sessionStatusLabel(s),
        JornadaAbierta: s.isOpen ? "Sí" : "No",
      })),
    [sessions],
  );

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Fichajes");
    XLSX.writeFile(wb, `fichajes_${from}_${to}.xlsx`);
  };

  const exportCsv = () => {
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fichajes_${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const showEmployeeCol =
    selectedUserId == null && sessions.some((s) => s.employee);

  const openCorrect = (row) => {
    setAdjustEntry(row);
    setAdjustMode("correct");
    setAdjustOpen(true);
  };

  const openRequestCorrect = (row) => {
    setRequestEntry(row);
    setRequestMode("correct");
    setRequestOpen(true);
  };

  const openAdd = () => {
    setAdjustEntry(null);
    setAdjustMode("add");
    setAdjustOpen(true);
  };

  const correctionColSpan =
    (showEmployeeCol ? 1 : 0) + (canRrhh || canFichaje ? 1 : 0) + 6;

  const showEmployeeHourChips =
    showEmployeeCol && workStats.employeeTotals.length > 1;
  const showDailyHourChips =
    !showEmployeeHourChips && workStats.dailyChips.length > 0;

  return (
    <Box>
      <TabContentShell accentColor={theme.palette.primary.main}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Box sx={ttToolbarSx(theme)}>
          <TextField
            label="Desde"
            type="date"
            size="small"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ ...ttToolbarFieldSx(theme), width: { xs: "100%", sm: 150 } }}
          />
          <TextField
            label="Hasta"
            type="date"
            size="small"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ ...ttToolbarFieldSx(theme), width: { xs: "100%", sm: 150 } }}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<SearchOutlinedIcon />}
            onClick={load}
            sx={ttToolbarButtonSx}
          >
            Filtrar
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshOutlinedIcon />}
            onClick={load}
            disabled={loading}
            sx={ttToolbarButtonSx}
          >
            Actualizar
          </Button>
          <Box sx={{ flex: 1, display: { xs: "none", sm: "block" } }} />
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadOutlinedIcon />}
            onClick={exportExcel}
            disabled={!sessions.length}
            sx={ttToolbarButtonSx}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={exportCsv}
            disabled={!sessions.length}
            sx={ttToolbarButtonSx}
          >
            CSV
          </Button>
          {canRrhh ? (
            <Button
              variant="contained"
              size="small"
              color="secondary"
              onClick={openAdd}
              sx={ttToolbarButtonSx}
            >
              Añadir olvidado
            </Button>
          ) : null}
        </Box>

        {!canRrhh && canFichaje ? (
          <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>
            Si te equivocaste u olvidaste fichar, puedes solicitar una
            corrección desde esta pantalla o en la pestaña Solicitudes. RRHH
            debe aprobarla antes de que se aplique al historial.
          </Alert>
        ) : null}

        <Stack spacing={1} sx={{ mb: 1.25 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              label={`${sessions.length} jornada(s)`}
              size="small"
              sx={ttKpiChipSx}
            />
            {workStats.totalMinutes > 0 ? (
              <Chip
                label={`Total: ${formatMinutes(workStats.totalMinutes)}`}
                size="small"
                color="primary"
                sx={{ ...ttKpiChipSx, fontWeight: 800 }}
              />
            ) : null}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: { xs: "none", sm: "block" }, width: "100%" }}
            >
              Una fila por jornada (entrada + salida). Las correcciones sustituyen
              el fichaje anterior sin borrarlo del sistema.
            </Typography>
          </Stack>

          {workStats.totalMinutes > 0 ? (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {showEmployeeHourChips
                ? workStats.employeeTotals.map((emp) => (
                    <Chip
                      key={emp.userId}
                      label={`${emp.username}: ${formatMinutes(emp.workedMinutes)}`}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ fontWeight: 700, height: 26 }}
                    />
                  ))
                : null}
              {showDailyHourChips
                ? workStats.dailyChips.map((day) => (
                    <Chip
                      key={day.dateStr}
                      label={`${day.label}: ${formatMinutes(day.minutes)}`}
                      size="small"
                      color={hoursChipColor(day.minutes)}
                      variant="outlined"
                      sx={{ fontWeight: 700, height: 26 }}
                    />
                  ))
                : null}
            </Stack>
          ) : null}
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : isMobile ? (
          <Stack spacing={1.5}>
            {sessions.map((session) => (
              <HistorySessionCard
                key={session.id}
                session={session}
                theme={theme}
                showEmployeeCol={showEmployeeCol}
                canRrhh={canRrhh}
                canFichaje={canFichaje}
                pendingEntryIds={pendingEntryIds}
                onCorrect={openCorrect}
                onRequestCorrect={openRequestCorrect}
                onGoToCorrections={onGoToCorrections}
              />
            ))}
            {!sessions.length ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  Sin registros en el periodo
                </Typography>
              </Box>
            ) : null}
          </Stack>
        ) : (
          <TableContainer sx={ttHistoryTableContainerSx(theme)}>
            <Table size="small" sx={ttHistoryTableSx}>
              <TableHead>
                <TableRow>
                  {showEmployeeCol ? <TableCell>Empleado</TableCell> : null}
                  <TableCell sx={{ width: 80 }}>Fecha</TableCell>
                  <TableCell>Entrada</TableCell>
                  <TableCell>Salida</TableCell>
                  <TableCell sx={{ width: 120 }}>Modalidad</TableCell>
                  <TableCell sx={{ width: 90 }}>Horas</TableCell>
                  <TableCell sx={{ width: 100 }}>Estado</TableCell>
                  {canRrhh || canFichaje ? (
                    <TableCell align="right" sx={{ width: 130 }}>
                      Acciones
                    </TableCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((session) => {
                  const sessionDate = formatDate(
                    session.clockIn?.recordedAt || session.clockOut?.recordedAt,
                  );
                  const workMode =
                    session.clockIn?.workMode || session.clockOut?.workMode || "—";
                  const employeeName =
                    session.employee?.username || String(session.userId);
                  const rowAccent = sessionAccentColor(session, theme);

                  return (
                    <TableRow
                      key={session.id}
                      hover
                      sx={
                        session.isOpen
                          ? {
                              bgcolor: alpha(theme.palette.info.main, 0.04),
                            }
                          : undefined
                      }
                    >
                      {showEmployeeCol ? (
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                fontSize: "0.68rem",
                                fontWeight: 800,
                                bgcolor: alpha(rowAccent, 0.14),
                                color: rowAccent,
                                border: "1px solid",
                                borderColor: alpha(rowAccent, 0.22),
                              }}
                            >
                              {employeeInitials(employeeName)}
                            </Avatar>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, lineHeight: 1.2 }}
                            >
                              {employeeName}
                            </Typography>
                          </Stack>
                        </TableCell>
                      ) : null}
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {sessionDate}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {entryCell(session.clockIn, theme, {
                          punchColor: theme.palette.success.main,
                          PunchIcon: LoginOutlinedIcon,
                        })}
                      </TableCell>
                      <TableCell>
                        {session.isOpen ? (
                          <Chip
                            label="Sin salida"
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{
                              height: 22,
                              fontWeight: 700,
                              fontSize: "0.68rem",
                              borderRadius: 1.25,
                            }}
                          />
                        ) : (
                          entryCell(session.clockOut, theme, {
                            punchColor: theme.palette.error.main,
                            PunchIcon: LogoutOutlinedIcon,
                          })
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Chip
                            label={workMode}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 22,
                              fontWeight: 700,
                              fontSize: "0.68rem",
                              textTransform: "capitalize",
                              borderRadius: 1.25,
                              width: "fit-content",
                            }}
                          />
                          {session.pauses.length > 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              {session.pauses.length} pausa(s)
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {session.workedMinutes > 0 ? (
                          <Chip
                            label={formatMinutes(session.workedMinutes)}
                            size="small"
                            color={hoursChipColor(session.workedMinutes)}
                            sx={{
                              height: 22,
                              fontWeight: 800,
                              fontSize: "0.68rem",
                              borderRadius: 1.25,
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={sessionStatusLabel(session)}
                          size="small"
                          color={sessionStatusColor(session)}
                          variant="outlined"
                          sx={{
                            height: 22,
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            borderRadius: 1.25,
                          }}
                        />
                      </TableCell>
                      {canRrhh ? (
                        <TableCell align="right">
                          <SessionActions
                            session={session}
                            canRrhh={canRrhh}
                            canFichaje={false}
                            pendingEntryIds={pendingEntryIds}
                            onCorrect={openCorrect}
                            onRequestCorrect={openRequestCorrect}
                            onGoToCorrections={onGoToCorrections}
                            theme={theme}
                          />
                        </TableCell>
                      ) : null}
                      {!canRrhh && canFichaje ? (
                        <TableCell align="right">
                          <SessionActions
                            session={session}
                            canRrhh={false}
                            canFichaje={canFichaje}
                            pendingEntryIds={pendingEntryIds}
                            onCorrect={openCorrect}
                            onRequestCorrect={openRequestCorrect}
                            onGoToCorrections={onGoToCorrections}
                            theme={theme}
                          />
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })}
                {!sessions.length ? (
                  <TableRow>
                    <TableCell
                      colSpan={correctionColSpan}
                      align="center"
                      sx={{ py: 4 }}
                    >
                      <Typography color="text.secondary">
                        Sin registros en el periodo
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabContentShell>

      <ClockAdjustDialog
        open={adjustOpen}
        mode={adjustMode}
        entry={adjustEntry}
        defaultUserId={selectedUserId}
        employees={employees}
        onClose={() => setAdjustOpen(false)}
        onSaved={load}
      />

      <CorrectionRequestDialog
        open={requestOpen}
        mode={requestMode}
        entry={requestEntry}
        onClose={() => setRequestOpen(false)}
        onSaved={load}
      />
    </Box>
  );
};

export default HistoryTab;
